/**
 * CSS Minify Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

let lightningInitPromise: Promise<unknown> | undefined;

async function getLightningCss() {
  const mod = await import('lightningcss-wasm');
  // Dynamic import interop — module shape varies by bundler
  const m = mod as Record<string, unknown>;
  const init = typeof m.default === 'function' ? m.default : typeof m.init === 'function' ? m.init : null;
  if (init) {
    if (lightningInitPromise == null) {
      if (typeof window !== 'undefined' && typeof fetch === 'function') {
        const wasmUrl = (await import('lightningcss-wasm/lightningcss_node.wasm?url')).default as string;
        lightningInitPromise = init(fetch(wasmUrl)) as Promise<unknown>;
      } else {
        lightningInitPromise = init() as Promise<unknown>;
      }
    }
    await lightningInitPromise;
  }
  return mod;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const CssMinifyTool: ITransformerTool = {
  id: 'css-minify',
  name: 'CSS Minify',
  description: 'Minify CSS using Lightning CSS (WASM)',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['css', 'minify', 'compress'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const css = String(input ?? '');
    if (!css.trim()) return '';

    try {
      const lightning = await getLightningCss();
      const out = lightning.transform({
        filename: 'input.css',
        code: encoder.encode(css),
        minify: true,
        sourceMap: false,
      });

      return decoder.decode(out.code).trim();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to minify CSS';
      throw ToolError.execution(message);
    }
  },
};
